import { useCallback, useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { useEscapeToClose } from "../../hooks/useEscapeToClose";

export interface PanelStep {
  id: string;
  title: string;
  howTo: string;
  link: { label: string; target: string };
  status: "todo" | "done" | "skipped";
}

export interface PanelView {
  workspace: PanelStep[];
  person: PanelStep[] | null;
  next: PanelStep | null;
  workspaceComplete: boolean;
  firstVisit: boolean;
}

interface GetStartedPanelProps {
  open: boolean;
  onClose: () => void;
  /** Called once when the server says this is the member's first sign-in. */
  onFirstVisit: () => void;
  /** Take the person to the screen a step's link names. */
  onNavigate: (target: string) => void;
}

const MARK: Record<PanelStep["status"], string> = { done: "✓", skipped: "–", todo: "○" };

export function GetStartedPanel({ open, onClose, onFirstVisit, onNavigate }: GetStartedPanelProps) {
  const [view, setView] = useState<PanelView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(open, onClose);

  const call = useCallback(async (path: string, method: "GET" | "POST"): Promise<PanelView | null> => {
    try {
      const res = await fetch(path, { method, headers: { ...(await getAuthHeader()), "Content-Type": "application/json" } });
      const body = await res.json();
      if (!res.ok) {
        setError(body?.error ?? "Could not load your checklist.");
        return null;
      }
      setError(null);
      return body as PanelView;
    } catch (err) {
      console.error("[Onboarding] request failed:", err);
      setError("Could not load your checklist.");
      return null;
    }
  }, []);

  // Load on mount (to catch a first sign-in) and each time the panel opens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await call("/api/workspace/onboarding", "GET");
        if (cancelled || !next) return;
        setView(next);
        if (next.firstVisit) onFirstVisit();
      } catch (err) {
        console.error("[Onboarding] load failed:", err);
      }
    })();
    return () => { cancelled = true; };
    // onFirstVisit is intentionally not a dependency: it must fire once per open, not per parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, call]);

  async function change(stepId: string, action: "skip" | "resume") {
    const next = await call(`/api/workspace/onboarding/steps/${stepId}/${action}`, "POST");
    if (next) setView(next);
  }

  if (!open) return null;

  const renderStep = (s: PanelStep) => (
    <li key={s.id} className="get-started-step" data-status={s.status}>
      <span className="get-started-mark" aria-hidden="true">{MARK[s.status]}</span>
      <span className="get-started-title">{s.id} {s.title}</span>
      {s.status === "todo" && (
        <button type="button" className="btn btn-ghost" onClick={() => change(s.id, "skip")}>Skip</button>
      )}
      {s.status === "skipped" && (
        <button type="button" className="btn btn-ghost" onClick={() => change(s.id, "resume")}>Resume</button>
      )}
    </li>
  );

  return (
    <>
      <div className="panel-scrim" style={{ zIndex: 8999 }} onClick={onClose} />
      <div className="welcome-page" onClick={onClose}>
        <div className="panel welcome-card" role="dialog" aria-modal="true" aria-labelledby="get-started-title" onClick={(e) => e.stopPropagation()}>
          <div className="panel-inner">
            <div className="panel-top">
              <span id="get-started-title">Get started</span>
              <button type="button" className="panel-x" aria-label="Close" onClick={onClose}>×</button>
            </div>
            <div className="welcome-body">
              {error && <p className="welcome-lede" role="alert">{error}</p>}
              {!view && !error && <p className="welcome-lede">Loading your checklist…</p>}
              {view && (
                <>
                  {view.workspaceComplete ? (
                    <p className="welcome-lede get-started-done">You're set up</p>
                  ) : null}
                  {view.next && (
                    <div className="stage-card get-started-next">
                      <strong>Next: {view.next.title}</strong>
                      <span>{view.next.howTo}</span>
                      <button type="button" className="btn" onClick={() => { onNavigate(view.next!.link.target); onClose(); }}>
                        {view.next.link.label}
                      </button>
                    </div>
                  )}
                  <h3>Your workspace</h3>
                  <ul className="get-started-list">{view.workspace.map(renderStep)}</ul>
                  {view.person && (
                    <>
                      <h3>You</h3>
                      <ul className="get-started-list">{view.person.map(renderStep)}</ul>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
